/**
 * Handstand alignment / balance metrics from densified torso + pose landmarks.
 * Pure functions — unit-testable without DOM or MediaPipe runtime.
 */

import type { Point, PoseLandmark, PoseMetrics, TorsoSegment } from "./types";
import { LM } from "./landmarks";
import {
  DEFAULT_INTERMEDIATE_COUNT,
  add,
  dot2,
  estimateSpineCurvature,
  intermediatePoints,
  length2,
  midpoint,
  normalize2,
  perp2,
  scale,
  sub,
  torsoFromLandmarks,
} from "./torso";

/**
 * Signed perpendicular offset of point P from infinite line A→B (image plane).
 * Positive is toward +perp2(B-A).
 */
export function signedOffsetFromLine(p: Point, a: Point, b: Point): number {
  const ab = sub(b, a);
  const len = length2(ab);
  if (len < 1e-9) return 0;
  const ap = sub(p, a);
  // 2D cross product magnitude / |ab|
  return (ab.x * ap.y - ab.y * ap.x) / len;
}

export function absOffsetFromLine(p: Point, a: Point, b: Point): number {
  return Math.abs(signedOffsetFromLine(p, a, b));
}

/**
 * Metrics derived purely from a densified torso segment.
 * Intermediate points between shoulders and hips drive banana/stack scores.
 */
export function metricsFromTorso(torso: TorsoSegment): Pick<
  PoseMetrics,
  | "bananaIndex"
  | "stackDeviation"
  | "maxMidDeviation"
  | "stackAngleDeg"
  | "intermediateMidline"
> {
  const mid = torso.midline;
  if (mid.length < 3) {
    throw new Error("Torso midline must include intermediate points between shoulders and hips");
  }
  const shoulder = mid[0];
  const hip = mid[mid.length - 1];
  const intermediates = intermediatePoints(mid);

  const signed = intermediates.map((p) => signedOffsetFromLine(p, shoulder, hip));
  const abs = signed.map((s) => Math.abs(s));

  const bananaIndex =
    signed.reduce((a, b) => a + b, 0) / Math.max(1, signed.length);
  const stackDeviation =
    abs.reduce((a, b) => a + b, 0) / Math.max(1, abs.length);
  const maxMidDeviation = abs.length ? Math.max(...abs) : 0;

  // Image coords: +y often down; vertical stack angle uses screen vertical.
  const chord = sub(hip, shoulder);
  const stackAngleDeg =
    (Math.atan2(chord.x, -chord.y) * 180) / Math.PI; // 0 ≈ vertical up→down or down→up

  return {
    bananaIndex,
    stackDeviation,
    maxMidDeviation,
    stackAngleDeg,
    intermediateMidline: intermediates,
  };
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function angleAt(a: Point, b: Point, c: Point): number {
  const ba = normalize2(sub(a, b));
  const bc = normalize2(sub(c, b));
  const d = Math.max(-1, Math.min(1, dot2(ba, bc)));
  return (Math.acos(d) * 180) / Math.PI;
}

/**
 * Full pose metrics: densify torso (with intermediate points), then score
 * alignment/balance cues for the coaching loop.
 */
export function computePoseMetrics(
  landmarks: PoseLandmark[],
  options: {
    intermediateCount?: number;
    spineCurvature?: number;
    torso?: TorsoSegment;
  } = {},
): PoseMetrics {
  const torso =
    options.torso ??
    torsoFromLandmarks(landmarks, {
      intermediateCount: options.intermediateCount ?? DEFAULT_INTERMEDIATE_COUNT,
      spineCurvature: options.spineCurvature,
    });

  const torsoMetrics = metricsFromTorso(torso);

  // When intermediates are collinear on the shoulder–hip chord (live tracking),
  // fold in joint-geometry banana so coaching still works without floating points.
  let bananaIndex = torsoMetrics.bananaIndex;
  let stackDeviation = torsoMetrics.stackDeviation;
  if (stackDeviation < 0.008) {
    const geo = estimateSpineCurvature(landmarks);
    bananaIndex = geo;
    stackDeviation = Math.max(stackDeviation, Math.abs(geo));
  }

  const ls = landmarks[LM.LEFT_SHOULDER];
  const rs = landmarks[LM.RIGHT_SHOULDER];
  const lh = landmarks[LM.LEFT_HIP];
  const rh = landmarks[LM.RIGHT_HIP];
  const le = landmarks[LM.LEFT_ELBOW];
  const re = landmarks[LM.RIGHT_ELBOW];
  const lw = landmarks[LM.LEFT_WRIST];
  const rw = landmarks[LM.RIGHT_WRIST];
  const nose = landmarks[LM.NOSE];
  const lk = landmarks[LM.LEFT_KNEE];
  const rk = landmarks[LM.RIGHT_KNEE];

  // Shoulder open: angle wrist–shoulder–hip closer to 180° is more open when inverted.
  let shoulderOpen = 0.5;
  if (ls && lh && lw && rs && rh && rw) {
    const left = angleAt(lw, ls, lh);
    const right = angleAt(rw, rs, rh);
    const avg = (left + right) / 2;
    // Map 90°→0, 180°→1
    shoulderOpen = clamp01((avg - 90) / 90);
  }

  // Elbow lock: wrist–elbow–shoulder near 180°.
  let elbowLock = 0.5;
  if (ls && le && lw && rs && re && rw) {
    const left = angleAt(lw, le, ls);
    const right = angleAt(rw, re, rs);
    const avg = (left + right) / 2;
    elbowLock = clamp01((avg - 140) / 40);
  }

  // Head extension proxy: nose vs shoulder mid relative to stack.
  let headExtension = 0.2;
  if (nose && ls && rs) {
    const shoulderMid = midpoint(ls, rs);
    const hipMid = midpoint(lh, rh);
    const chord = sub(hipMid, shoulderMid);
    const n = normalize2(perp2(chord));
    const offset = Math.abs(dot2(sub(nose, shoulderMid), n));
    headExtension = clamp01(offset / 0.12);
  }

  // Hip pike: shoulder–hip–knee angle (smaller = more pike).
  let hipPike = 0.1;
  if (ls && lh && lk && rs && rh && rk) {
    const left = angleAt(ls, lh, lk);
    const right = angleAt(rs, rh, rk);
    const avg = (left + right) / 2;
    // Straight ~180, deep pike ~90
    hipPike = clamp01((180 - avg) / 90);
  }

  // Inverted: hips above shoulders in image space? (y smaller) OR wrists near
  // bottom and head near bottom relative to ankles.
  const shoulderMid = midpoint(ls, rs);
  const hipMid = midpoint(lh, rh);
  // In browser image coords y grows downward; freestanding HS often has hips
  // with smaller y than shoulders when upright on hands... actually inverted
  // person: hands at large y (bottom), feet at small y (top). Hips y < shoulders y.
  const vertical = shoulderMid.y - hipMid.y; // positive if hips above shoulders on screen
  const inverted = clamp01(0.5 + vertical * 2.5);

  // Balance proxies from wrist vs shoulder projection when inverted.
  let overbalance = 0;
  let underbalance = 0;
  if (lw && rw && inverted > 0.35) {
    const wristMid = midpoint(lw, rw);
    // In side view, horizontal offset of shoulders vs wrists indicates lean.
    const dx = shoulderMid.x - wristMid.x;
    // Without known camera side, use magnitude split by sign of banana-ish lean.
    if (dx > 0) {
      overbalance = clamp01(Math.abs(dx) / 0.08);
    } else {
      underbalance = clamp01(Math.abs(dx) / 0.08);
    }
    // Also use stack angle as a balance cue.
    const ang = Math.abs(torsoMetrics.stackAngleDeg);
    if (ang > 12) {
      overbalance = Math.max(overbalance, clamp01((ang - 12) / 40));
    }
  }

  // Overall quality: reward inversion + low stack deviation + mild hollow + open shoulders.
  const bananaPenalty = Math.min(1, Math.abs(bananaIndex) / 0.1);
  const stackPenalty = Math.min(1, stackDeviation / 0.12);
  const overallQuality = clamp01(
    inverted * 0.35 +
      shoulderOpen * 0.2 +
      elbowLock * 0.15 +
      (1 - bananaPenalty) * 0.15 +
      (1 - stackPenalty) * 0.15,
  );

  return {
    ...torsoMetrics,
    bananaIndex,
    stackDeviation,
    shoulderOpen,
    elbowLock,
    headExtension,
    hipPike,
    overbalance,
    underbalance,
    inverted,
    overallQuality,
  };
}

/**
 * Synthetic inverted handstand landmarks for demos/tests.
 * Clean vertical stack by default; `banana` bows the line in image-x (arch).
 */
export function syntheticHandstandLandmarks(options: {
  banana?: number;
  shoulderOpen?: number;
  cx?: number;
  cy?: number;
  scale?: number;
} = {}): PoseLandmark[] {
  const cx = options.cx ?? 0.5;
  // Vertical center of the figure (mid-torso)
  const cy = options.cy ?? 0.5;
  const s = options.scale ?? 0.2;
  const banana = options.banana ?? 0;
  const open = options.shoulderOpen ?? 1;

  // Inverted: wrists at bottom (high y), feet at top (low y).
  // Half-width of the body stack (shoulders ≈ hips for a clean line).
  const halfW = 0.055;
  const wristHalf = halfW * (0.55 + 0.45 * open);
  // Banana: shift hips/knees/ankles to one side progressively
  const hipShift = banana * 0.55;
  const kneeShift = banana * 0.9;
  const ankleShift = banana * 1.1;

  const landmarks: PoseLandmark[] = Array.from({ length: 33 }, () => ({
    x: cx,
    y: cy,
    z: 0,
    visibility: 1,
  }));

  const set = (i: number, x: number, y: number, z = 0) => {
    landmarks[i] = { x, y, z, visibility: 1 };
  };

  // Arms (bottom of image)
  set(LM.LEFT_WRIST, cx - wristHalf, cy + s * 1.05);
  set(LM.RIGHT_WRIST, cx + wristHalf, cy + s * 1.05);
  set(LM.LEFT_ELBOW, cx - wristHalf, cy + s * 0.62);
  set(LM.RIGHT_ELBOW, cx + wristHalf, cy + s * 0.62);
  set(LM.LEFT_SHOULDER, cx - halfW, cy + s * 0.2);
  set(LM.RIGHT_SHOULDER, cx + halfW, cy + s * 0.2);

  // Head (between arms / just “below” shoulders when inverted)
  set(LM.NOSE, cx + banana * 0.02, cy + s * 0.42);
  set(LM.LEFT_EAR, cx - 0.02, cy + s * 0.38);
  set(LM.RIGHT_EAR, cx + 0.02, cy + s * 0.38);
  set(LM.LEFT_EYE, cx - 0.015, cy + s * 0.4);
  set(LM.RIGHT_EYE, cx + 0.015, cy + s * 0.4);

  // Hips / legs (top of image)
  set(LM.LEFT_HIP, cx - halfW + hipShift, cy - s * 0.45);
  set(LM.RIGHT_HIP, cx + halfW + hipShift, cy - s * 0.45);
  set(LM.LEFT_KNEE, cx - halfW * 0.55 + kneeShift, cy - s * 1.05);
  set(LM.RIGHT_KNEE, cx + halfW * 0.55 + kneeShift, cy - s * 1.05);
  set(LM.LEFT_ANKLE, cx - halfW * 0.35 + ankleShift, cy - s * 1.55);
  set(LM.RIGHT_ANKLE, cx + halfW * 0.35 + ankleShift, cy - s * 1.55);
  set(LM.LEFT_HEEL, cx - halfW * 0.35 + ankleShift, cy - s * 1.58);
  set(LM.RIGHT_HEEL, cx + halfW * 0.35 + ankleShift, cy - s * 1.58);
  set(LM.LEFT_FOOT_INDEX, cx - halfW * 0.35 + ankleShift, cy - s * 1.68);
  set(LM.RIGHT_FOOT_INDEX, cx + halfW * 0.35 + ankleShift, cy - s * 1.68);

  return landmarks;
}

/** Re-export helper for metric tests that want an explicit offset mid-chain. */
export function offsetMidlineIntermediates(
  torso: TorsoSegment,
  signedOffset: number,
): TorsoSegment {
  const shoulder = torso.midline[0];
  const hip = torso.midline[torso.midline.length - 1];
  const nrm = normalize2(perp2(sub(hip, shoulder)));
  const mid = torso.midline.map((p, i) => {
    if (i === 0 || i === torso.midline.length - 1) return p;
    // Peak at center
    const t = i / (torso.midline.length - 1);
    const w = Math.sin(Math.PI * t) * signedOffset;
    return add(p, scale(nrm, w));
  });
  return { ...torso, midline: mid };
}
